/**
 * PipelineRunsTable Component
 *
 * Displays a table of ADF pipeline runs with status, duration, and error information
 */

import { useState } from "react";
import { useLocation } from "wouter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Clock, Ban, Loader2, ExternalLink } from "lucide-react";
import { format } from "date-fns";

interface PipelineRun {
  runId: string;
  pipelineName: string;
  status: 'Succeeded' | 'Failed' | 'InProgress' | 'Cancelled' | 'Queued';
  runStart: string;
  runEnd: string | null;
  durationInMs: number | null;
  message: string | null;
  parameters: Record<string, any>;
  lastTableProcessed?: string | null;
  failedActivities?: any[];
  totalRowsRead?: number;
  totalRowsCopied?: number;
  totalRowsSkipped?: number;
  childRunsSummary?: {
    total: number;
    succeeded: number;
    failed: number;
    inProgress: number;
    cancelled: number;
  };
}

interface PipelineRunsTableProps {
  runs: PipelineRun[];
  isLoading: boolean;
}

export function PipelineRunsTable({ runs, isLoading }: PipelineRunsTableProps) {
  const [selectedRun, setSelectedRun] = useState<PipelineRun | null>(null);
  const [, setLocation] = useLocation();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Succeeded':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'Failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'InProgress':
        return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'Cancelled':
        return <Ban className="h-4 w-4 text-gray-600" />;
      case 'Queued':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      Succeeded: "default",
      Failed: "destructive",
      InProgress: "secondary",
      Cancelled: "outline",
      Queued: "secondary",
    };

    return (
      <Badge variant={variants[status] || "outline"} className="flex items-center gap-1">
        {getStatusIcon(status)}
        {status}
      </Badge>
    );
  };

  const formatDuration = (durationInMs: number | null) => {
    if (!durationInMs) return 'N/A';

    const seconds = Math.floor(durationInMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const getTableName = (run: PipelineRun): string => {
    if (run.parameters?.TableName) {
      return run.parameters.TableName;
    }
    if (run.lastTableProcessed) {
      return run.lastTableProcessed;
    }
    return 'N/A';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No pipeline runs found in the selected time range
      </div>
    );
  }

  const renderChildRunsSummary = (summary?: PipelineRun['childRunsSummary']) => {
    if (!summary || summary.total === 0) {
      return <span className="text-muted-foreground text-sm">—</span>;
    }

    return (
      <div className="text-sm">
        <span className="text-green-600 font-medium">{summary.succeeded}</span>
        {summary.failed > 0 && (
          <>
            {' / '}
            <span className="text-red-600 font-medium">{summary.failed}</span>
          </>
        )}
        {summary.inProgress > 0 && (
          <>
            {' / '}
            <span className="text-blue-600 font-medium">{summary.inProgress}</span>
          </>
        )}
        <span className="text-muted-foreground ml-1">of {summary.total}</span>
      </div>
    );
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Pipeline</TableHead>
              <TableHead>Table</TableHead>
              <TableHead>Child Runs</TableHead>
              <TableHead>Start Time</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead className="text-right">Rows</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((run) => (
              <TableRow key={run.runId} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedRun(run)}>
                <TableCell>{getStatusBadge(run.status)}</TableCell>
                <TableCell className="font-medium">{run.pipelineName}</TableCell>
                <TableCell>{getTableName(run)}</TableCell>
                <TableCell>{renderChildRunsSummary(run.childRunsSummary)}</TableCell>
                <TableCell>{format(new Date(run.runStart), 'MMM d, HH:mm:ss')}</TableCell>
                <TableCell>{formatDuration(run.durationInMs)}</TableCell>
                <TableCell className="text-right">
                  {run.status === 'Failed' && run.totalRowsCopied !== undefined ? (
                    <span className="text-sm">
                      {run.totalRowsCopied?.toLocaleString() || 0}
                      {run.totalRowsSkipped && run.totalRowsSkipped > 0 && (
                        <span className="text-orange-600 ml-1">
                          (-{run.totalRowsSkipped.toLocaleString()})
                        </span>
                      )}
                    </span>
                  ) : 'N/A'}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {run.childRunsSummary && run.childRunsSummary.total > 0 && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/adf/monitor?parentRun=${run.runId}`, '_blank');
                        }}
                      >
                        View {run.childRunsSummary.total} Child Runs
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedRun(run); }}>
                      Details
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Run Details Dialog */}
      <Dialog open={selectedRun !== null} onOpenChange={(open) => !open && setSelectedRun(null)}>
        <DialogContent className="max-w-4xl md:max-w-4xl lg:max-w-4xl max-h-[85vh] overflow-y-auto overflow-x-hidden p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center gap-2 text-lg">
              Pipeline Run Details
              {selectedRun && getStatusIcon(selectedRun.status)}
            </DialogTitle>
            <DialogDescription className="text-xs break-all">
              Run ID: {selectedRun?.runId}
            </DialogDescription>
          </DialogHeader>

          {selectedRun && (
            <div className="space-y-6 pt-2 w-full overflow-x-hidden">
              {/* Child Runs Summary - PROMINENTLY AT TOP */}
              {selectedRun.childRunsSummary && selectedRun.childRunsSummary.total > 0 && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 w-full overflow-x-hidden">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="text-base font-semibold text-blue-900">Child Pipeline Runs</div>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => {
                        window.open(`/adf/monitor?parentRun=${selectedRun.runId}`, '_blank');
                      }}
                    >
                      View All {selectedRun.childRunsSummary.total} Child Runs →
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    <div className="bg-white rounded-md p-3 text-center">
                      <div className="text-xs text-muted-foreground">Total</div>
                      <div className="text-2xl font-bold">{selectedRun.childRunsSummary.total}</div>
                    </div>
                    <div className="bg-white rounded-md p-3 text-center">
                      <div className="text-xs text-muted-foreground">Succeeded</div>
                      <div className="text-2xl font-bold text-green-600">{selectedRun.childRunsSummary.succeeded}</div>
                    </div>
                    <div className="bg-white rounded-md p-3 text-center">
                      <div className="text-xs text-muted-foreground">Failed</div>
                      <div className="text-2xl font-bold text-red-600">{selectedRun.childRunsSummary.failed}</div>
                    </div>
                    <div className="bg-white rounded-md p-3 text-center">
                      <div className="text-xs text-muted-foreground">In Progress</div>
                      <div className="text-2xl font-bold text-blue-600">{selectedRun.childRunsSummary.inProgress}</div>
                    </div>
                    <div className="bg-white rounded-md p-3 text-center">
                      <div className="text-xs text-muted-foreground">Cancelled</div>
                      <div className="text-2xl font-bold text-gray-600">{selectedRun.childRunsSummary.cancelled}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Basic Info */}
              <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full overflow-x-hidden">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pipeline</div>
                  <div className="text-sm font-medium mt-1 break-words">{selectedRun.pipelineName}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</div>
                  <div className="mt-1">{getStatusBadge(selectedRun.status)}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Start Time</div>
                  <div className="text-sm font-medium mt-1">{format(new Date(selectedRun.runStart), 'PPpp')}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Duration</div>
                  <div className="text-sm font-medium mt-1">{formatDuration(selectedRun.durationInMs)}</div>
                </div>
              </div>

              {/* Parameters */}
              {selectedRun.parameters && Object.keys(selectedRun.parameters).length > 0 && (
                <div className="w-full overflow-x-hidden">
                  <div className="text-sm font-medium text-muted-foreground mb-2">Parameters</div>
                  <div className="bg-muted rounded-md p-3 text-sm font-mono overflow-x-auto">
                    {Object.entries(selectedRun.parameters).map(([key, value]) => (
                      <div key={key} className="break-words">
                        <span className="text-blue-600">{key}</span>: {JSON.stringify(value)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Row Statistics */}
              {selectedRun.totalRowsRead !== undefined && (
                <div className="w-full overflow-x-hidden">
                  <div className="text-sm font-semibold text-gray-700 mb-3">Row Statistics</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Rows Read</div>
                      <div className="text-2xl font-bold mt-2">{selectedRun.totalRowsRead?.toLocaleString() || 0}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <div className="text-xs font-medium text-green-700 uppercase tracking-wide">Rows Copied</div>
                      <div className="text-2xl font-bold text-green-600 mt-2">{selectedRun.totalRowsCopied?.toLocaleString() || 0}</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <div className="text-xs font-medium text-orange-700 uppercase tracking-wide">Rows Skipped</div>
                      <div className="text-2xl font-bold text-orange-600 mt-2">{selectedRun.totalRowsSkipped?.toLocaleString() || 0}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Failed Activities */}
              {selectedRun.failedActivities && selectedRun.failedActivities.length > 0 && (
                <div className="border-t pt-4 w-full overflow-x-hidden">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 w-full">
                    <div className="text-base font-semibold text-red-600">
                      Failed Activities ({selectedRun.failedActivities.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Close dialog and navigate to Failed Tables tab
                          setSelectedRun(null);
                          // Use window hash to navigate to tab
                          window.location.hash = 'failed';
                        }}
                      >
                        View All Failed Tables
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          // Copy detailed failed activities summary with FULL error messages
                          const header = `Failed Pipeline Run: ${selectedRun.pipelineName}\nRun ID: ${selectedRun.runId}\nFailed at: ${selectedRun.runStart}\n\n`;
                          const failedSummary = selectedRun.failedActivities!
                            .map((a, i) => `${i + 1}. ${a.tableName || a.activityName}\n   Category: ${a.errorCategory}\n   Error Code: ${a.error.errorCode}\n   Full Error Message:\n   ${a.error.message}\n\n`)
                            .join('\n');
                          navigator.clipboard.writeText(header + failedSummary);

                          // Show toast or alert
                          alert('Copied ' + selectedRun.failedActivities!.length + ' failed activities with full error messages to clipboard');
                        }}
                      >
                        Copy Full Errors ({selectedRun.failedActivities.length})
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3 w-full overflow-x-hidden">
                    {selectedRun.failedActivities.map((activity, idx) => (
                      <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-4 w-full overflow-x-hidden">
                        <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm break-words">{activity.activityName}</div>
                            <div className="text-xs text-muted-foreground mt-1">{activity.activityType}</div>
                            {activity.tableName && (
                              <div className="text-xs font-medium text-gray-700 mt-1 break-words">Table: {activity.tableName}</div>
                            )}
                          </div>
                          <Badge variant="destructive" className="shrink-0">{activity.errorCategory}</Badge>
                        </div>
                        {activity.error && (
                          <div className="mt-3 w-full overflow-x-hidden">
                            <div className="text-xs font-medium text-gray-600 mb-1 break-words">Error Code: {activity.error.errorCode}</div>
                            <div className="text-xs font-mono bg-white p-3 rounded border border-red-300 max-h-40 overflow-y-auto overflow-x-auto leading-relaxed whitespace-pre-wrap break-words w-full">
                              {activity.error.message}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Message */}
              {selectedRun.message && (
                <div className="w-full overflow-x-hidden">
                  <div className="text-sm font-medium text-muted-foreground mb-2">Message</div>
                  <div className="bg-muted rounded-md p-3 text-sm break-words whitespace-pre-wrap">{selectedRun.message}</div>
                </div>
              )}

              {/* Azure Portal Link */}
              <div className="pt-4 border-t">
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={`https://portal.azure.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View in Azure Portal
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
